import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Input,
  Select,
  Form,
  Space,
  Typography,
  Tag,
  Alert,
  Divider,
  Switch,
  InputNumber,
  Tooltip,
  message,
  Modal,
  Table,
  Badge,
  Tree,
  Progress,
  Statistic,
  Drawer,
  Popconfirm,
  Empty,
  Descriptions,
  QRCode,
  Upload,
  AutoComplete,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  SaveOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  CopyOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
  HomeOutlined,
  ApartmentOutlined,
  DatabaseOutlined,
  BoxPlotOutlined,
  BarsOutlined,
  FolderOpenOutlined,
  FolderOutlined,
  BuildOutlined,
  ScanOutlined,
  BarChartOutlined,
  HeatMapOutlined,
  ExportOutlined,
  ImportOutlined,
  DownOutlined,
  RightOutlined,
  SearchOutlined,
  FilterOutlined,
  SettingOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { DataNode } from 'antd/es/tree';
import Warehouse3DView from './Warehouse3DView';
import apiService from '../../../services/apiService';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { Search } = Input;
const { DirectoryTree } = Tree;

// 位置管理接口
interface WarehouseLocation {
  id: string;
  warehouseId: string;
  code: string;
  name: string;
  type: 'ZONE' | 'FLOOR' | 'AISLE' | 'RACK' | 'SHELF' | 'BIN' | 'PACKING_ZONE';
  parentId?: string;
  level: number;
  capacity?: number;
  usedCapacity: number;
  isActive: boolean;
  coordinates: { x: number; y: number; z?: number };
  children?: WarehouseLocation[];
  parentPath?: string;
  utilizationRate?: number;
  autoGenerated?: boolean;
  ruleId?: string;
  description?: string;
  dimensions?: {
    length: number;
    width: number;
    height: number;
    unit: 'cm' | 'm';
  };
  restrictions?: {
    maxWeight: number;
    temperatureRange?: { min: number; max: number };
    hazmatAllowed: boolean;
    accessLevel: 'public' | 'restricted' | 'private';
  };
  equipment?: {
    rfidEnabled: boolean;
    scannerInstalled: boolean;
    cameraMonitored: boolean;
    automationLevel: 'manual' | 'semi' | 'full';
  };
  performance?: {
    pickingEfficiency: number;
    errorRate: number;
    accessFrequency: number;
    lastAccessed?: string;
  };
  created_at: string;
  updated_at: string;
}

// 倉庫接口
interface UnifiedWarehouse {
  id: string;
  name: string;
  code: string;
  type: string;
  location: any;
  capacity: any;
  zones: any[];
  locationRules: any[];
  technology: any;
  performance: any;
  status: string;
  created_at: string;
  updated_at: string;
}

// 位置統計接口
interface LocationStats {
  totalLocations: number;
  activeLocations: number;
  utilizationRate: number;
  capacityUsed: number;
  totalCapacity: number;
  byType: {
    [key: string]: {
      count: number;
      utilization: number;
      capacity: number;
    };
  };
  performanceMetrics: {
    averagePickingTime: number;
    errorRate: number;
    accessFrequency: number;
  };
}

interface LocationManagementProps {
  warehouse: UnifiedWarehouse;
  onLocationUpdate?: (locations: WarehouseLocation[]) => void;
  // 可選：由父組件注入的外部位置數據（例如從編碼向導生成）
  externalLocations?: WarehouseLocation[];
  // 每次變更遞增以觸發刷新
  applyToken?: number;
}

const LocationManagement: React.FC<LocationManagementProps> = ({
  warehouse,
  onLocationUpdate,
  externalLocations,
  applyToken,
}) => {
  // 狀態管理
  const [locations, setLocations] = useState<WarehouseLocation[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<WarehouseLocation | null>(null);
  const [editingLocation, setEditingLocation] = useState<WarehouseLocation | null>(null);
  const [stats, setStats] = useState<LocationStats | null>(null);
  
  // UI 狀態
  const [modalVisible, setModalVisible] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [batchModalVisible, setBatchModalVisible] = useState(false);
  const [viewMode, setViewMode] = useState<'tree' | 'table' | 'grid' | '3d'>('tree');
  const [searchText, setSearchText] = useState('');
  const [filterType, setFilterType] = useState<'ALL' | WarehouseLocation['type']>('ALL');
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  // 通道節點的分頁狀態：parentId -> { page, pageSize, total }
  const [backendWarning, setBackendWarning] = useState<string | null>(null);
  
  // 表單
  const [locationForm] = Form.useForm();
  const [batchForm] = Form.useForm();

  // 位置類型配置
  const locationTypeConfig = {
    ZONE: { 
      label: '區域', 
      icon: <ApartmentOutlined />, 
      color: 'blue', 
      level: 1,
      description: '倉庫內的主要功能區域',
      maxChildren: 50,
    },
    FLOOR: {
      label: '樓層',
      icon: <BuildOutlined />,
      color: 'gold',
      level: 2,
      description: '倉庫樓層',
      maxChildren: 200,
    },
    AISLE: { 
      label: '通道', 
      icon: <BarsOutlined />, 
      color: 'green', 
      level: 3,
      description: '區域內的通道或走道',
      maxChildren: 100,
    },
    RACK: { 
      label: '貨架', 
      icon: <DatabaseOutlined />, 
      color: 'orange', 
      level: 4,
      description: '通道內的貨架結構',
      maxChildren: 20,
    },
    SHELF: { 
      label: '層', 
      icon: <BoxPlotOutlined />, 
      color: 'purple', 
      level: 5,
      description: '貨架上的層級',
      maxChildren: 50,
    },
    BIN: { 
      label: '儲位', 
      icon: <HomeOutlined />, 
      color: 'red', 
      level: 6,
      description: '最小的存儲單位',
      maxChildren: 0,
    },
    PACKING_ZONE: { 
      label: '包裝區', 
      icon: <BoxPlotOutlined />, 
      color: 'magenta', 
      level: 1,
      description: '專門的包裝作業區域',
      maxChildren: 10,
    },
  };

  // 兜底配置（當後端返回了尚未配置的 locationType 時避免報錯）
  const fallbackTypeConfig = {
    label: '其他',
    icon: <FolderOutlined />,
    color: 'default',
    level: 0,
    description: '未知類型',
    maxChildren: 0,
  } as const;

  // 初始化數據
  useEffect(() => {
    if (!warehouse) return;
    if (externalLocations && externalLocations.length > 0) {
      setLocations(externalLocations);
      calculateStats(externalLocations);
      // 嘗試自動展開部分節點
      const keysToExpand = externalLocations
        .filter(l => typeof l.level === 'number' && l.level <= 2)
        .map(l => l.id)
        .slice(0, 10);
      setExpandedKeys(keysToExpand);
    } else {
      loadLocationData();
    }
  }, [warehouse, externalLocations, applyToken]);

  // 加載位置數據（改為調用後端 API）
  const loadLocationData = async () => {
    try {
      const resp: any = await apiService.getWarehouseLocations(warehouse.id, { flat: true, level: 2 });
      if (resp && typeof resp === 'object' && resp.warning) {
        setBackendWarning(String(resp.warning));
      } else {
        setBackendWarning(null);
      }
      const rows: any[] = Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []);
      // 後端返回列到前端模型的簡單映射
      const mapped: WarehouseLocation[] = rows.map((r: any) => ({
        id: String(r.id),
        warehouseId: String(r.warehouseId || r.warehouse_id || warehouse.id),
        code: String(r.code || r.location_code || ''),
        name: String(r.name || r.location_name || ''),
        type: String(r.type || r.location_type || 'BIN') as any,
        parentId: r.parentId ?? r.parent_id ?? undefined,
        level: Number(r.level || 1),
        capacity: r.capacity !== null && r.capacity !== undefined ? Number(r.capacity) : undefined,
        usedCapacity: Number(r.usedCapacity ?? r.used_capacity ?? 0),
        isActive: r.isActive ?? r.is_active ?? true,
        coordinates: { x: Number(r.x || 0), y: Number(r.y || 0), z: r.z !== undefined ? Number(r.z) : undefined },
        utilizationRate: r.utilizationRate ?? r.utilization_rate ?? undefined,
        description: r.description || undefined,
        dimensions: (r.dim_length || r.dim_width || r.dim_height) ? { length: Number(r.dim_length||0), width: Number(r.dim_width||0), height: Number(r.dim_height||0), unit: (r.dim_unit || 'm') } : undefined,
        restrictions: (r.restr_max_weight || r.restr_temp_min || r.restr_temp_max || r.restr_hazmat_allowed !== undefined || r.restr_access_level) ? {
          maxWeight: Number(r.restr_max_weight||0),
          temperatureRange: (r.restr_temp_min!==undefined || r.restr_temp_max!==undefined) ? { min: Number(r.restr_temp_min||0), max: Number(r.restr_temp_max||0) } : undefined,
          hazmatAllowed: Boolean(r.restr_hazmat_allowed),
          accessLevel: (r.restr_access_level || 'public') as any,
        } : undefined,
        equipment: (r.equip_rfid_enabled !== undefined || r.equip_scanner_installed !== undefined || r.equip_camera_monitored !== undefined || r.equip_automation_level) ? {
          rfidEnabled: Boolean(r.equip_rfid_enabled),
          scannerInstalled: Boolean(r.equip_scanner_installed),
          cameraMonitored: Boolean(r.equip_camera_monitored),
          automationLevel: (r.equip_automation_level || 'manual') as any,
        } : undefined,
        performance: (r.perf_picking_efficiency !== undefined || r.perf_error_rate !== undefined || r.perf_access_frequency !== undefined || r.perf_last_accessed) ? {
          pickingEfficiency: Number(r.perf_picking_efficiency||0),
          errorRate: Number(r.perf_error_rate||0),
          accessFrequency: Number(r.perf_access_frequency||0),
          lastAccessed: r.perf_last_accessed || undefined,
        } : undefined,
        created_at: r.created_at || r.createdAt || new Date().toISOString(),
        updated_at: r.updated_at || r.updatedAt || new Date().toISOString(),
      }));

      setLocations(mapped);
      calculateStats(mapped);
      // 自動展開前 10 個層級<=2 的節點（避免初始載入過深）
      const keysToExpand = mapped.filter(l => typeof l.level === 'number' && l.level <= 2).map(l => l.id).slice(0, 10);
      setExpandedKeys(keysToExpand);
    } catch (err: any) {
      console.error('load locations error:', err);
      message.error(err?.message || '加載位置數據失敗');
    }
  };

  // 計算統計數據
  const calculateStats = (locationData: WarehouseLocation[]) => {
    const totalLocations = locationData.length;
    const activeLocations = locationData.filter(l => l.isActive).length;
    const totalCapacity = locationData.reduce((sum, l) => sum + (l.capacity || 0), 0);
    const capacityUsed = locationData.reduce((sum, l) => sum + l.usedCapacity, 0);
    const utilizationRate = totalCapacity > 0 ? (capacityUsed / totalCapacity) * 100 : 0;

    // 按類型統計
    const byType: LocationStats['byType'] = {};
    Object.keys(locationTypeConfig).forEach(type => {
      const typeLocations = locationData.filter(l => l.type === type);
      const typeCapacity = typeLocations.reduce((sum, l) => sum + (l.capacity || 0), 0);
      const typeUsed = typeLocations.reduce((sum, l) => sum + l.usedCapacity, 0);
      
      byType[type] = {
        count: typeLocations.length,
        utilization: typeCapacity > 0 ? (typeUsed / typeCapacity) * 100 : 0,
        capacity: typeCapacity,
      };
    });

    // 性能指標
    const performanceData = locationData.filter(l => l.performance);
    const performanceMetrics = {
      averagePickingTime: performanceData.length > 0 
        ? performanceData.reduce((sum, l) => sum + (l.performance?.pickingEfficiency || 0), 0) / performanceData.length
        : 0,
      errorRate: performanceData.length > 0 
        ? performanceData.reduce((sum, l) => sum + (l.performance?.errorRate || 0), 0) / performanceData.length
        : 0,
      accessFrequency: performanceData.length > 0 
        ? performanceData.reduce((sum, l) => sum + (l.performance?.accessFrequency || 0), 0) / performanceData.length
        : 0,
    };

    const stats: LocationStats = {
      totalLocations,
      activeLocations,
      utilizationRate,
      capacityUsed,
      totalCapacity,
      byType,
      performanceMetrics,
    };

    setStats(stats);
  };

  // 構建樹形數據
  const buildTreeData = (locations: WarehouseLocation[]): DataNode[] => {
    const locationMap = new Map<string, WarehouseLocation>();
    locations.forEach(location => {
      locationMap.set(location.id, location);
    });

    const buildNode = (location: WarehouseLocation): DataNode => {
      const config = (locationTypeConfig as any)[location.type] || fallbackTypeConfig;
      const children = locations
        .filter(l => l.parentId === location.id)
        .map(child => buildNode(child));

      return {
        key: location.id,
        title: (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Space>
                {config.icon}
                <Text strong>{location.code}</Text>
                {(() => {
                  const code = (location.code || '').trim();
                  const name = (location.name || '').trim();
                  const showName = name.length > 0 && name.toLowerCase() !== code.toLowerCase();
                  return showName ? <Text type="secondary">{name}</Text> : null;
                })()}
                <Tag color={config.color}>{config.label}</Tag>
                {location.utilizationRate && (
                  <Progress 
                    percent={Math.round(location.utilizationRate)} 
                    size="small" 
                    style={{ width: 80 }}
                  />
                )}
              </Space>
            </div>
            <div>
              <Space>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {location.usedCapacity}/{location.capacity || 0}
                </Text>
                <Badge 
                  status={location.isActive ? 'success' : 'error'} 
                  text={location.isActive ? '活躍' : '停用'}
                />
              </Space>
            </div>
          </div>
        ),
        children: children.length > 0 ? children : undefined,
        isLeaf: location.type === 'BIN',
      };
    };

    const rootLocations = locations.filter(l => !l.parentId || l.level === 1);
    // 若後端只返回扁平列表但缺少父子關係，可基於層級與編碼嘗試構建父子鏈接
    if (rootLocations.length === locations.length && locations.some(l => l.parentId)) {
      // 已有 parentId，直接返回
      return rootLocations.map(location => buildNode(location));
    }
    if (locations.length > 0 && locations.every(l => !l.parentId)) {
      // 嘗試基於 code 規則推斷父子（例如 A / A01 / R01 / L01 / P01）
      const codeToId = new Map<string, string>();
      locations.forEach(l => codeToId.set(l.code, l.id));
      const withParentGuessed = locations.map(l => {
        if (l.parentId) return l;
        const parts = l.code.split(/[-_.]/);
        if (parts.length > 1) {
          const parentCode = parts.slice(0, parts.length - 1).join('-');
          const parentId = codeToId.get(parentCode);
          if (parentId) {
            return { ...l, parentId };
          }
        }
        return l;
      });
      const roots = withParentGuessed.filter(l => !l.parentId || l.level === 1);
      const map = new Map<string, WarehouseLocation>();
      withParentGuessed.forEach(l => map.set(l.id, l));
      const buildGuessed = (loc: WarehouseLocation): DataNode => {
        const cfg = (locationTypeConfig as any)[loc.type] || fallbackTypeConfig;
        const children = withParentGuessed.filter(x => x.parentId === loc.id).map(buildGuessed);
        return {
          key: loc.id,
          title: (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Space>
                  {cfg.icon}
                  <Text strong>{loc.code}</Text>
                <Tag color={cfg.color}>{cfg.label}</Tag>
                </Space>
              </div>
              <div>
                <Space>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {loc.usedCapacity}/{loc.capacity || 0}
                  </Text>
                  <Badge status={loc.isActive ? 'success' : 'error'} text={loc.isActive ? '活躍' : '停用'} />
                </Space>
              </div>
            </div>
          ),
          children: children.length > 0 ? children : undefined,
          icon: cfg.icon,
          isLeaf: loc.type === 'BIN',
        };
      };
      return roots.map(buildGuessed);
    }
    return rootLocations.map(location => buildNode(location));
  };

  // 過濾位置數據
  const getFilteredLocations = () => {
    // 基礎集合
    const all = locations;
    let base = all;

    // 類型過濾（先縮小範圍）
    if (filterType !== 'ALL') {
      base = base.filter(l => l.type === filterType);
    }

    // 關鍵字過濾 + 補齊祖先節點，確保樹能顯示
    if (searchText && searchText.trim()) {
      const q = searchText.toLowerCase();
      const idToNode = new Map<string, WarehouseLocation>();
      all.forEach(l => idToNode.set(l.id, l));

      // 先在 base 集合中做匹配
      const matched = new Set<string>();
      base.forEach(l => {
        if (l.code.toLowerCase().includes(q) || l.name.toLowerCase().includes(q)) {
          matched.add(l.id);
        }
      });

      // 把匹配節點的所有祖先補齊（使用 all 作為來源，避免祖先被過濾掉）
      const include = new Set<string>(matched);
      const addAncestors = (nodeId: string) => {
        const node = idToNode.get(nodeId);
        if (!node || !node.parentId) return;
        if (!include.has(node.parentId)) {
          include.add(node.parentId);
          addAncestors(node.parentId);
        }
      };
      matched.forEach(addAncestors);

      // 最終輸出集合：在所有節點中選擇 id 在 include 內，並同時滿足類型過濾
      const setForType = filterType === 'ALL' ? new Set(all.map(l => l.id)) : new Set(base.map(l => l.id));
      const result = all.filter(l => include.has(l.id) && setForType.has(l.id));
      return result;
    }

    return base;
  };

  // 表格列定義
  const locationColumns: ColumnsType<WarehouseLocation> = [
    {
      title: '位置信息',
      key: 'info',
      render: (_, record) => {
        const config = (locationTypeConfig as any)[record.type] || fallbackTypeConfig;
        return (
          <div>
            <Space>
              {config.icon}
              <Text strong>{record.code}</Text>
              <Tag color={config.color}>{config.label}</Tag>
            </Space>
            <br />
            <Text type="secondary">{record.name}</Text>
            {record.description && (
              <>
                <br />
                <Text type="secondary" style={{ fontSize: '12px' }}>{record.description}</Text>
              </>
            )}
          </div>
        );
      },
    },
    {
      title: '層級路徑',
      key: 'path',
      render: (_, record) => {
        const getParentPath = (location: WarehouseLocation): string[] => {
          const path = [location.code];
          if (location.parentId) {
            const parent = locations.find(l => l.id === location.parentId);
            if (parent) {
              path.unshift(...getParentPath(parent));
            }
          }
          return path;
        };
        
        const path = getParentPath(record);
        return (
          <div>
            {path.map((code, index) => (
              <span key={index}>
                {index > 0 && <Text type="secondary"> {'>'} </Text>}
                <Text code>{code}</Text>
              </span>
            ))}
          </div>
        );
      },
    },
    {
      title: '容量使用',
      key: 'capacity',
      render: (_, record) => {
        const rate = record.capacity ? (record.usedCapacity / record.capacity) * 100 : 0;
        return (
          <div>
            <Progress 
              percent={Math.round(rate)} 
              size="small" 
              strokeColor={rate > 90 ? '#ff4d4f' : rate > 70 ? '#faad14' : '#52c41a'}
            />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.usedCapacity} / {record.capacity || 0}
            </Text>
          </div>
        );
      },
    },
    {
      title: '坐標位置',
      key: 'coordinates',
      render: (_, record) => (
        <Text code style={{ fontSize: '12px' }}>
          X:{record.coordinates.x}, Y:{record.coordinates.y}
          {record.coordinates.z !== undefined && `, Z:${record.coordinates.z}`}
        </Text>
      ),
    },
    {
      title: '性能指標',
      key: 'performance',
      render: (_, record) => {
        if (!record.performance) return '-';
        return (
          <div style={{ fontSize: '12px' }}>
            <div>效率: {record.performance.pickingEfficiency}%</div>
            <div>錯誤率: {record.performance.errorRate}%</div>
            <div>訪問頻率: {record.performance.accessFrequency}</div>
          </div>
        );
      },
    },
    {
      title: '狀態',
      dataIndex: 'isActive',
      key: 'status',
      render: (isActive) => (
        <Badge 
          status={isActive ? 'success' : 'error'} 
          text={isActive ? '活躍' : '停用'}
        />
      ),
    },
    {
      title: '操作',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            type="link" 
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedLocation(record);
              setDrawerVisible(true);
            }}
          />
          <Button 
            type="link" 
            icon={<EditOutlined />}
            onClick={() => {
              setEditingLocation(record);
              locationForm.setFieldsValue(record);
              setModalVisible(true);
            }}
          />
          <Button 
            type="link" 
            icon={<PlusOutlined />}
            onClick={() => {
              const config = locationTypeConfig[record.type];
              if (config.maxChildren > 0) {
                setEditingLocation(null);
                locationForm.setFieldsValue({
                  parentId: record.id,
                  warehouseId: warehouse.id,
                  level: record.level + 1,
                });
                setModalVisible(true);
              } else {
                message.warning('此類型位置不能添加子位置');
              }
            }}
            disabled={record.type === 'BIN'}
          />
          <Popconfirm
            title="確定要刪除這個位置嗎？"
            onConfirm={() => {
              const newLocations = locations.filter(l => 
                l.id !== record.id && l.parentId !== record.id
              );
              setLocations(newLocations);
              calculateStats(newLocations);
              message.success('位置刪除成功');
            }}
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 處理位置表單提交（改為呼叫後端保存）
  const handleLocationSubmit = () => {
    locationForm.validateFields().then(async (values) => {
      try {
        // 計算最終層級：若選了父級，則必須比父級層級大 1（至少）
        const parent = values.parentId ? locations.find(l => l.id === values.parentId) : undefined;
        const requestedLevel = Number(values.level || 1);
        const effectiveLevel = parent ? Math.max(requestedLevel, Number(parent.level || 1) + 1) : 1;
        const companyId =
          localStorage.getItem('companyId') || localStorage.getItem('currentCompanyId') || undefined;

        const payload = {
          warehouseId: String(warehouse.id),
          locationCode: values.code,
          locationName: values.name,
          locationType: values.type,
          parentId: values.parentId || undefined,
          level: effectiveLevel,
          capacity: values.capacity !== undefined ? Number(values.capacity) : undefined,
          companyId,
        };

        // 若為編輯模式暫仍採用本地更新；新增則調用後端創建
        if (!editingLocation) {
          await apiService.post('/warehouse-management/locations', payload as any);
        } else {
          // 可擴充為調用對應的 PUT 接口（若後端提供），暫時採本地回寫
        }

        // 重新載入列表以與後端一致
        await loadLocationData();

        setModalVisible(false);
        locationForm.resetFields();
        setEditingLocation(null);
        message.success(`位置${editingLocation ? '更新' : '創建'}成功`);
      } catch (err: any) {
        console.error('save location error:', err);
        message.error(err?.message || '保存位置失敗');
      }
    });
  };

  // 渲染統計概覽
  const renderStatsOverview = () => {
    if (!stats) return null;

    return (
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="總位置數"
              value={stats.totalLocations}
              prefix={<DatabaseOutlined style={{ color: '#1890ff' }} />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="活躍位置"
              value={stats.activeLocations}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="容量使用率"
              value={stats.utilizationRate}
              precision={1}
              suffix="%"
              prefix={<BarChartOutlined style={{ color: '#faad14' }} />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="已用容量"
              value={stats.capacityUsed}
              suffix={`/ ${stats.totalCapacity}`}
              prefix={<BoxPlotOutlined style={{ color: '#722ed1' }} />}
            />
          </Card>
        </Col>
      </Row>
    );
  };

  // 渲染類型統計
  const renderTypeStats = () => {
    if (!stats) return null;

    return (
      <Card title="按類型統計" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          {Object.entries(stats.byType).map(([type, data]) => {
            const config = (locationTypeConfig as any)[type] || fallbackTypeConfig;
            if (data.count === 0) return null;
            
            return (
              <Col span={8} key={type}>
                <Card size="small">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {config.icon}
                      <Text strong style={{ marginLeft: 8 }}>{config.label}</Text>
                      <Badge count={data.count} style={{ marginLeft: 'auto' }} />
                    </div>
                    <Progress 
                      percent={Math.round(data.utilization)} 
                      size="small"
                      strokeColor={config.color}
                    />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      容量: {data.capacity.toLocaleString()}
                    </Text>
                  </Space>
                </Card>
              </Col>
            );
          })}
        </Row>
      </Card>
    );
  };

  // 主渲染
  return (
    <div>
      {/* 統計概覽 */}
      {renderStatsOverview()}
      
      {/* 類型統計 */}
      {renderTypeStats()}

      {/* 操作欄 */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col span={6}>
            <Search
              placeholder="搜索位置代碼或名稱"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={(v) => setSearchText(v)}
              allowClear
            />
          </Col>
          <Col span={4}>
            <Select
              value={filterType}
              onChange={setFilterType}
              style={{ width: '100%' }}
              placeholder="篩選類型"
              allowClear
            >
              <Option value="ALL">全部類型</Option>
              {Object.entries(locationTypeConfig).map(([type, config]) => (
                <Option key={type} value={type}>
                  <Space>
                    {config.icon}
                    {config.label}
                  </Space>
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={4}>
            <Select
              value={viewMode}
              onChange={setViewMode}
              style={{ width: '100%' }}
            >
              <Option value="tree">樹狀視圖</Option>
              {/*<<Option value="table">表格視圖</Option>
              Option value="grid">網格視圖</Option>
              <Option value="3d">3D視圖</Option>*/}
            </Select>
          </Col>
          <Col span={10} style={{ textAlign: 'right' }}>
            <Space>
              {/*<Button
                icon={<ThunderboltOutlined />}
                onClick={() => setBatchModalVisible(true)}
              >
                批量操作
              </Button>*/}
              <Button
                icon={<ExportOutlined />}
                onClick={() => message.info('導出功能開發中')}
              >
                導出數據
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditingLocation(null);
                  locationForm.setFieldsValue({
                    warehouseId: warehouse.id,
                    level: 1,
                    isActive: true,
                  });
                  setModalVisible(true);
                }}
              >
                新增位置
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* 主內容區域 */}
      <Card>
        {backendWarning && (
          <Alert
            type="warning"
            showIcon
            message="後端暫不可用，顯示為空"
            description={backendWarning}
            style={{ marginBottom: 12 }}
          />
        )}
        {viewMode === 'tree' && (
          <DirectoryTree
            multiple
            showIcon={false}
            showLine
            virtual
            height={560}
            expandedKeys={expandedKeys}
            onExpand={setExpandedKeys}
            loadData={async (treeNode) => {
              const nodeId = String(treeNode.key);
              // 若該節點已經有 children，則不再請求
              const alreadyHasChildren = locations.some(l => l.parentId === nodeId);
              if (alreadyHasChildren) return;
              try {
                const companyId =
                  localStorage.getItem('companyId') || localStorage.getItem('currentCompanyId') || undefined;
                const pageSize = 100;
                let page = 1;
                let total: number | undefined = undefined;
                const aggregated: WarehouseLocation[] = [];

                // 懶加載：循環直至載滿所有子節點
                // 透過 total 判斷是否需要繼續；若後端未返回 total，則依據 pageSize 判斷
                // 避免過多請求，限制最多 20 頁
                const maxLoops = 20;
                let loops = 0;
                while (loops < maxLoops) {
                  const resp: any = await apiService.getWarehouseLocations(warehouse.id, {
                    flat: true,
                    parentId: nodeId,
                    page,
                    pageSize,
                    companyId,
                  });
                  const rows: any[] = Array.isArray(resp?.data)
                    ? resp.data
                    : Array.isArray(resp)
                      ? resp
                      : [];

                  const mapped: WarehouseLocation[] = rows.map((r: any) => ({
                    id: String(r.id),
                    warehouseId: String(r.warehouseId || r.warehouse_id || warehouse.id),
                    code: String(r.code || r.location_code || ''),
                    name: String(r.name || r.location_name || ''),
                    type: String(r.type || r.location_type || 'BIN') as any,
                    parentId: r.parentId ?? r.parent_id ?? nodeId,
                    level: Number(r.level || 1),
                    capacity: r.capacity !== null && r.capacity !== undefined ? Number(r.capacity) : undefined,
                    usedCapacity: Number(r.usedCapacity ?? r.used_capacity ?? 0),
                    isActive: r.isActive ?? r.is_active ?? true,
                    coordinates: { x: Number(r.x || 0), y: Number(r.y || 0), z: r.z !== undefined ? Number(r.z) : undefined },
                    utilizationRate: r.utilizationRate ?? r.utilization_rate ?? undefined,
                    description: r.description || undefined,
                    created_at: r.created_at || r.createdAt || new Date().toISOString(),
                    updated_at: r.updated_at || r.updatedAt || new Date().toISOString(),
                  }));

                  aggregated.push(...mapped);

                  if (typeof resp?.total === 'number') {
                    total = resp.total;
                    if (aggregated.length >= resp.total) break;
                  }

                  if (rows.length < pageSize) break;
                  page += 1;
                  loops += 1;
                }

                  setLocations(prev => {
                    const merged = [...prev];
                    const existed = new Set(prev.map(p => p.id));
                    aggregated.forEach(m => { if (!existed.has(m.id)) merged.push(m); });
                    return merged;
                  });

              } catch (e) {
                console.error('lazy load children error', e);
              }
            }}
            treeData={buildTreeData(getFilteredLocations())}
            onSelect={(keys, info) => {
              if (keys.length > 0) {
                const location = locations.find(l => l.id === keys[0]);
                if (location) {
                  setSelectedLocation(location);
                  setDrawerVisible(true);
                }
              }
            }}
          />
        )}
        
        {viewMode === 'table' && (
          <Table
            columns={locationColumns}
            dataSource={getFilteredLocations()}
            rowKey="id"
            pagination={{ pageSize: 20 }}
            scroll={{ x: true }}
          />
        )}
        
        {viewMode === 'grid' && (
          <Row gutter={[16, 16]}>
            {getFilteredLocations().map(location => {
              const config = (locationTypeConfig as any)[location.type] || fallbackTypeConfig;
              return (
                <Col span={6} key={location.id}>
                  <Card
                    hoverable
                    size="small"
                    title={
                      <Space>
                        {config.icon}
                        <Text strong>{location.code}</Text>
                      </Space>
                    }
                    extra={
                      <Badge 
                        status={location.isActive ? 'success' : 'error'} 
                      />
                    }
                    actions={[
                      <EyeOutlined 
                        key="view" 
                        onClick={() => {
                          setSelectedLocation(location);
                          setDrawerVisible(true);
                        }}
                      />,
                      <EditOutlined 
                        key="edit"
                        onClick={() => {
                          setEditingLocation(location);
                          locationForm.setFieldsValue(location);
                          setModalVisible(true);
                        }}
                      />,
                      <DeleteOutlined 
                        key="delete" 
                        onClick={() => {
                          Modal.confirm({
                            title: '確定要刪除這個位置嗎？',
                            onOk: () => {
                              const newLocations = locations.filter(l => l.id !== location.id);
                              setLocations(newLocations);
                              calculateStats(newLocations);
                              message.success('位置刪除成功');
                            },
                          });
                        }}
                      />,
                    ]}
                  >
                    <div>
                      <Text type="secondary">{location.name}</Text>
                      <br />
                      <Tag color={config.color}>{config.label}</Tag>
                      {location.utilizationRate && (
                        <>
                          <br />
                          <Progress 
                            percent={Math.round(location.utilizationRate)} 
                            size="small" 
                            style={{ marginTop: 8 }}
                          />
                        </>
                      )}
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
        
        {viewMode === '3d' && (
          <Warehouse3DView 
            warehouse={warehouse}
            locations={getFilteredLocations()}
            onLocationSelect={(locationId) => {
              const location = locations.find(l => l.id === locationId);
              if (location) {
                setSelectedLocation(location);
                setDrawerVisible(true);
              }
            }}
          />
        )}
      </Card>

      {/* 位置編輯模態框 */}
      <Modal
        title={editingLocation ? '編輯位置' : '新增位置'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingLocation(null);
          locationForm.resetFields();
        }}
        onOk={handleLocationSubmit}
        width={800}
      >
        <Form form={locationForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="位置代碼" name="code" rules={[{ required: true }]}>
                <Input placeholder="輸入位置代碼" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="位置名稱" name="name" rules={[{ required: true }]}>
                <Input placeholder="輸入位置名稱" />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="位置類型" name="type" rules={[{ required: true }]}>
                <Select placeholder="選擇位置類型">
                  {Object.entries(locationTypeConfig).map(([type, config]) => (
                    <Option key={type} value={type}>
                      <Space>
                        {config.icon}
                        {config.label}
                      </Space>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="父級位置" name="parentId">
                <Select placeholder="選擇父級位置" allowClear>
                  {locations
                    .filter(l => l.type !== 'BIN')
                    .map(location => (
                      <Option key={location.id} value={location.id}>
                        {location.code} - {location.name}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="層級" name="level">
                <InputNumber min={1} max={5} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="總容量" name="capacity">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="已用容量" name="usedCapacity">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="狀態" name="isActive" valuePropName="checked">
                <Switch checkedChildren="活躍" unCheckedChildren="停用" />
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item label="描述" name="description">
            <Input.TextArea rows={3} placeholder="輸入位置描述" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 位置詳情抽屜 */}
      <Drawer
        title="位置詳情"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        width={600}
      >
        {selectedLocation && (
          <div>
            <Descriptions title="基本信息" bordered column={2}>
              <Descriptions.Item label="位置代碼">
                <Text code>{selectedLocation.code}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="位置名稱">
                {selectedLocation.name}
              </Descriptions.Item>
              <Descriptions.Item label="位置類型">
                <Space>
                  {(locationTypeConfig as any)[selectedLocation.type]?.icon || fallbackTypeConfig.icon}
                  <Tag color={(locationTypeConfig as any)[selectedLocation.type]?.color || 'default'}>
                    {(locationTypeConfig as any)[selectedLocation.type]?.label || fallbackTypeConfig.label}
                  </Tag>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="狀態">
                <Badge 
                  status={selectedLocation.isActive ? 'success' : 'error'} 
                  text={selectedLocation.isActive ? '活躍' : '停用'}
                />
              </Descriptions.Item>
              <Descriptions.Item label="容量使用" span={2}>
                <Progress 
                  percent={selectedLocation.capacity ? 
                    Math.round((selectedLocation.usedCapacity / selectedLocation.capacity) * 100) : 0
                  } 
                />
                <Text type="secondary">
                  {selectedLocation.usedCapacity} / {selectedLocation.capacity || 0}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="坐標位置" span={2}>
                <Text code>
                  X: {selectedLocation.coordinates.x}, 
                  Y: {selectedLocation.coordinates.y}
                  {selectedLocation.coordinates.z !== undefined && `, Z: ${selectedLocation.coordinates.z}`}
                </Text>
              </Descriptions.Item>
              {selectedLocation.description && (
                <Descriptions.Item label="描述" span={2}>
                  {selectedLocation.description}
                </Descriptions.Item>
              )}
            </Descriptions>

            {selectedLocation.performance && (
              <Card title="性能指標" style={{ marginTop: 16 }}>
                <Row gutter={16}>
                  <Col span={8}>
                    <Statistic
                      title="揀貨效率"
                      value={selectedLocation.performance.pickingEfficiency}
                      suffix="%"
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="錯誤率"
                      value={selectedLocation.performance.errorRate}
                      suffix="%"
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="訪問頻率"
                      value={selectedLocation.performance.accessFrequency}
                      suffix="次/天"
                    />
                  </Col>
                </Row>
              </Card>
            )}

            {selectedLocation.equipment && (
              <Card title="設備配置" style={{ marginTop: 16 }}>
                <Row gutter={16}>
                  <Col span={12}>
                    <Space direction="vertical">
                      <div>
                        <Badge 
                          status={selectedLocation.equipment.rfidEnabled ? 'success' : 'default'} 
                          text="RFID 啟用"
                        />
                      </div>
                      <div>
                        <Badge 
                          status={selectedLocation.equipment.scannerInstalled ? 'success' : 'default'} 
                          text="掃描器安裝"
                        />
                      </div>
                    </Space>
                  </Col>
                  <Col span={12}>
                    <Space direction="vertical">
                      <div>
                        <Badge 
                          status={selectedLocation.equipment.cameraMonitored ? 'success' : 'default'} 
                          text="攝像頭監控"
                        />
                      </div>
                      <div>
                        <Text>自動化程度: {selectedLocation.equipment.automationLevel}</Text>
                      </div>
                    </Space>
                  </Col>
                </Row>
              </Card>
            )}

            <Card title="位置二維碼" style={{ marginTop: 16 }}>
              <div style={{ textAlign: 'center' }}>
                <QRCode value={selectedLocation.code} />
                <br />
                <Text type="secondary">掃描查看位置信息</Text>
              </div>
            </Card>
          </div>
        )}
      </Drawer>

      {/* 批量操作模態框 */}
      {/*<Modal
        title="批量操作"
        open={batchModalVisible}
        onCancel={() => setBatchModalVisible(false)}
        footer={null}
        width={600}
      >
        <Alert
          message="批量操作功能"
          description="支持批量導入、編輯和刪除位置數據"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        
        <Space direction="vertical" style={{ width: '100%' }}>
          <Button 
            block 
            icon={<ImportOutlined />}
            onClick={() => message.info('批量導入功能開發中')}
          >
            批量導入位置
          </Button>
          <Button 
            block 
            icon={<ExportOutlined />}
            onClick={() => message.info('批量導出功能開發中')}
          >
            導出位置模板
          </Button>
          <Button 
            block 
            icon={<ThunderboltOutlined />}
            onClick={() => message.info('自動生成功能開發中')}
          >
            自動生成儲位
          </Button>
        </Space>
      </Modal>*/}
    </div>
  );
};

export default LocationManagement;